import {ReactSession} from "../utils/ReactSession.jsx";
import {AES, enc} from "crypto-js";
import Header from "./Header.jsx";
import {useState, useEffect, Suspense} from "react";
import useSound from 'use-sound';
import ApiRequester from "./ApiRequester.jsx";
import emergSound from "../sound/emergency_tone.mp3"
import {
    durationToNow, getTTL, getUTCTimestamp, sortFunc, campusToGuid, userToGuid, handleSubmitActions,
    formSuccess, formFail, iAmHigherAuthority, setSoundsMute, isBannerPresent, showModal
} from "../libs.jsx";
import Button from "./component-assets/Button.jsx";
import {CSVLink} from "react-csv";
import {toast, ToastContainer} from "react-toastify";
import Footer from "./Footer.jsx";
import TableHeaders from "./component-assets/TableHeaders.jsx";
import CampusDropDown from "./component-assets/CampusDropDown.jsx";
import AlertSyncStatus from "./component-assets/AlertSyncStatus.jsx";
import {Link} from "react-router-dom";
import Navigation from "./Navigation.jsx";
import TableBody from "./component-assets/TableBody.jsx";
import { useNotifications } from "../contexts/NotificationContext.jsx";


const CurrentAlertsV2 = (props) => {

    const { activeAlarms, isConnected, connectionError, isDataStale, registerAlertViewer, unregisterAlertViewer } = useNotifications();

    // Register as alert viewer so the provider enables periodic reconciliation while this page is mounted
    useEffect(() => { registerAlertViewer(); return () => unregisterAlertViewer(); }, [registerAlertViewer, unregisterAlertViewer]);

    let dData, headerData, csvHeaders, maintHeaderData, csvMaintHeaders, lostHeaderData;

    const [emergencySound] = useSound(emergSound, {volume: .5});

    headerData = [
        {label: "Event Date / Time", key: "EventDateTime", align: "center", type: "datetime-utc", sorts: true,
            border: {enabled: true, borderType: "left", borderColorKey: "AlertType", borderWidth: "6px"}},
        {label: "Duration", key: "EventDateTime", align: "center", type: "duration", sorts: true},
        {label: "Type", key: ["AlertType", "AlertSubType"], align: "center", sorts: true, type: "stack-muted"},
        {label: "Residents", key: "ResidentsObject", align: "center", filtered: false, sorts: false,
            link: {enabled: true, linkto: "/profiles/resident", linkkey: "resident_id", type: "obj", displayKey: ["firstName", "lastName"]}},
        {label: "Device / Serial #", key: ["DeviceUse", "SerialNumber"], align: "center", sorts: false,
            link: {enabled: true, type: "stack", linkto: "/profiles/inovonics", linkkey: "DeviceId"}},
        {label: "Location / Desc", key: ["LocationName", "LocationArea"], align: "center", sorts: false,
            link: {enabled: true, type: "stack", linkto: "/profiles/area", linkkey: "LocationId"}},
        {label: "Near", key: "NearName", align: "center", sorts: true},
        {label: "Accepted", key: "AcceptUsername", align: "center", sorts: true},
        {label: "Status", key: "Status", type: "alert-status", align: "center", sorts: true,
            status: {className: {Waiting: "text-warning", Overdue: "text-danger", Accepted: "text-primary",
                                 Cleared: "text-primary", Arrived: "text-primary", Default: "text-primary"}},
            popover: {body: {Waiting: "Waiting for alert to be accepted or cleared if accept feature is N/A for device.",
                             Overdue: "Alert has been active over 20 minutes.",
                             Accepted: "Alert has been accepted by a staff member but the device is still in alarm state.",
                             Cleared: "Device has been cleared. If this device is listed for AFR in system settings, it must be accepted and finished through the mobile app.",
                             Arrived: "Staff has accepted the alert and the device's alarm state has been cleared. Alert must be finished to close out.",
                             Default: ""
                            }
                      }
        }
    ];

    lostHeaderData = [
        {label: "Last Active", key: "last_active", align: "center", type: "datetime", sorts: true},
        {label: "Name", key: "device_name", align: "center", filtered: true, sorts: true,
            link: {enabled: true, type: "conditional", conditionKey: "manufacture_id",
                condition: {"1": {linkto: "/profiles/inovonics", linkkey: "device_id"},
                            "4": {linkto: "/profiles/palcare", linkkey: "device_id"}}}},
        {label: "Description", key: "descriptions", align: "center", filtered: true, sorts: true},
        {label: "Serial Number", key: "manufacture_uuid", align: "center", filtered: true, sorts: true},
        {label: "Residents", key: "residents", align: "center", filtered: true, filterKey: "resident_name",
            link: {enabled: true, linkto: "/profiles/resident", linkkey: "resident_id", type: "obj", displayKey: ["resident_name"]}},
        {label: "Area", key: "area_name", align: "center", filtered: true, sorts: true,
            link: {enabled: true, type: "standard", linkto: "/profiles/area", linkkey: "area_id"}},
    ];

    maintHeaderData = [
        {label: "Event Date / Time", key: "EventDateTime", align: "center", type: "datetime-utc", sorts: true,
            border: {enabled: true, borderType: "left", borderColorKey: "AlertType", borderWidth: "6px"}},
        {label: "Duration", key: "EventDateTime", align: "center", type: "duration", sorts: true},
        {label: "Category", key: ["AlertType", "AlertSubType"], align: "center", sorts: true, type: "stack-muted"},
        {label: "Residents", key: "ResidentsObject", align: "center", filtered: false, sorts: false,
            link: {enabled: true, linkto: "/profiles/resident", linkkey: "id", type: "obj", displayKey: ["fName", "lName"]}},
        {label: "Device / Serial #", key: ["DeviceUse", "SerialNumber"], align: "center", type: "stack", sorts: false},
        {label: "Location / Near", key: ["LocationName", "LocationArea"], align: "center", type: "stack", sorts: false},
        {label: "Status", key: "Status", type: "alert-status", align: "center", sorts: true,
            status: {className: {Waiting: "text-warning", Overdue: "text-danger", Accepted: "text-primary",
                                 Cleared: "text-primary", Arrived: "text-primary", Default: "text-primary"}},
            popover: {body: {Waiting: "Waiting for alert to be accepted or cleared if accept feature is N/A for device.",
                             Overdue: "Alert has been active over 20 minutes.",
                             Accepted: "Alert has been accepted by a staff member but the device is still in alarm state.",
                             Cleared: "Device has been cleared. If this device is listed for AFR in system settings, it must be accepted and finished through the mobile app.",
                             Arrived: "Staff has accepted the alert and the device's alarm state has been cleared. Alert must be finished to close out.",
                             Default: ""
                            }
                      }
        },
        {label: "Action", key: "AlarmId", align: "center", type: "custom-btn", sorts: false}
    ];

    csvHeaders = [
        {label: "Event Time", key: "EventDateTime"},
        {label: "Duration (seconds)", key: "Elapsed"},
        {label: "Alert Category", key: "AlertType"},
        {label: "Alert Type", key: "AlertSubType"},
        {label: "Residents", key: "Resident_Names"},
        {label: "Device", key: "DeviceUse"},
        {label: "Location", key: "LocationName"},
        {label: "Near", key: "NearName"},
        {label: "Status", key: "Status"}
    ];

    if (ReactSession.get("PAL") != null){
        let bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
        dData = JSON.parse(bData.toString(enc.Utf8));
    }

    const [lowBatData, setLowBatData] = useState([]);
    const [otherData, setOtherData] = useState([]);
    const [lostData, setLostData] = useState([]);
    const [emergRows, setEmergRows] = useState([]);
    const [lowBatRows, setLowBatRows] = useState([]);
    const [lostRows, setLostRows] = useState([]);
    const [otherRows, setOtherRows] = useState([]);
    const [tabItem, setTabItem] = useState("emergency");
    const [fullscreen, setFullScreen] = useState(false);
    const [sorter, setSorter] = useState({
        sortColumn: "EventDateTime",
        sortOrder: "asc"
    });
    const [lostSorter, setLostSorter] = useState({
        sortColumn: "last_active",
        sortOrder: "asc"
    });
    const [soundOff, setSoundOff] = useState(dData.soundsOff);
    const [soundInit, setSoundInit] = useState(true);
    const [alertKeyList, setAlertKeyList] = useState([]);
    const [count, setCount] = useState(0);
    const [lostCount, setLostCount] = useState(0);
    const [filteredEmergCount, setFilteredEmergCount] = useState(0);
    const [campusId, setCampusId] = useState(dData.acnt);
    const [csvLink, setLink] = useState({
        filename: "alarms.csv",
        headers: [],
        data: []
    });
    const [csvButton, setCSVButton] = useState(
        <CSVLink {...csvLink} uFEFF={false}>
                <Button text={"Export"} icon={true} iconClass={"fe fe-download me-2"} class={"btn btn-primary ms-2 px-4 mt-1"}/>
            </CSVLink> ? props?.toFetch === "maintenance" : ""
        )

    useEffect(() => {
        setCount(count => 0);
        setLostCount(count => 0);
    }, [props.toFetch]);

    useEffect(() => {
        let interval, toFetch, tmp, ie;
        interval = null;
        interval = setInterval(() => {
             setCount(count => count - 1);
        }, 1000);
        if (count <= 0){
            ApiRequester({reqEndpoint: "get-alerts", CampusId: campusToGuid(dData, campusId),
                                mode: fullscreen ? "station" : "standard", hybrid: dData?.hybrid,
                                legacy: dData?.legacy, new: dData?.new, ToGet: "maint",
                                UserId: userToGuid(dData, dData.user), Migrated: dData.migrated}).then(data => {
                let tmpLow = [];
                let tmpOther = [];
                if (data.length > 0){
                    for (ie=0; ie < data.length; ie++){
                        if (["low battery", "critbattery"].includes(data[ie].AlertSubType.toLowerCase())){
                            tmpLow.push(data[ie]);
                        }else if (["communication lost"].includes(data[ie].AlertSubType.toLowerCase())){

                        }else{
                            tmpOther.push(data[ie]);
                        }
                    }
                    setLowBatData(tmpLow);
                    setOtherData(tmpOther);
                }else{
                    setLowBatData([]);
                    setOtherData([]);
                }
            });
            setCount((count) => 60);
        }
        return () => clearInterval(interval);
    }, [count]);

    useEffect(() => {
        let interval = null;
        interval = setInterval(() => {
             setLostCount(lostCount => lostCount - 1);
        }, 1000);
        if (lostCount <= 0){
            ApiRequester({reqEndpoint: "myfetch", payload: {device_filter: "", campus_id: campusId}, "read-only": true,
                                procedure: "sp_getDeviceOffline", reqType: "stored"}).then(data => {
                setLostData(data);
            });
            setLostCount(lostCount => 120);
        }
        return () => clearInterval(interval);
    }, [lostCount]);

    useEffect(() => {
        setCount(0);
        setLostCount(0);
    }, [fullscreen]);

    useEffect(() => {
        const emergData = activeAlarms || [];
        let playSound = false, tmpKeyList = [];
        const myGroupGuids = dData?.groupGuids ? dData.groupGuids : [];
        const showAllByRole = dData && iAmHigherAuthority(10, dData.role_weight);
        let filteredEmerg = showAllByRole
            ? [...emergData]
            : emergData.filter(alarm => {
                const alarmGroupIds = alarm.GroupIds ?? [];
                return alarmGroupIds.some(gid => myGroupGuids?.includes(gid));
            });
        if (fullscreen) {
            filteredEmerg = filteredEmerg.filter(alarm => !alarm?.AckDateTime);
        }
        for (let i=0; i<filteredEmerg.length; i++){
            tmpKeyList = alertKeyList;
            if (!tmpKeyList?.includes(filteredEmerg[i].AlarmId)){
                if (!soundOff){
                    playSound = true;
                }
                tmpKeyList.push(filteredEmerg[i].AlarmId);
            }
        }
        setAlertKeyList(tmpKeyList);
        if (playSound) {
            emergencySound();
        }
        setFilteredEmergCount(filteredEmerg?.length ?? 0);

        if (tabItem === "emergency"){
            const filteredEmergSorted = [...filteredEmerg];
            filteredEmergSorted.sort((a, b) => sortFunc(a, b, sorter.sortColumn, sorter.sortOrder));
            setEmergRows(<TableBody start={0} end={filteredEmergSorted?.length ? filteredEmergSorted.length : 0} tableData={filteredEmergSorted}
                                checkbox={false} writeAccess={false} unk={"e-"} checkidkey={"AlarmId"}
                                actionAppearance={() => {}} tableHeaders={headerData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        }else if (tabItem === "lowbat"){
            lowBatData.sort((a, b) => sortFunc(a, b, sorter.sortColumn, sorter.sortOrder));
            setLowBatRows(<TableBody start={0} end={lowBatData?.length ? lowBatData.length : 0} tableData={lowBatData}
                                checkbox={false} writeAccess={false} unk={"b-"} checkidkey={"AlarmId"}
                                actionAppearance={() => {}} tableHeaders={maintHeaderData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                customBtnText={"Clear"} customBtnFunc={clearMaint}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        }else if (tabItem === "other"){
            otherData.sort((a, b) => sortFunc(a, b, sorter.sortColumn, sorter.sortOrder));
            setOtherRows(<TableBody start={0} end={otherData?.length ? otherData.length : 0} tableData={otherData}
                                checkbox={false} writeAccess={false} unk={"o-"} checkidkey={"AlarmId"}
                                actionAppearance={() => {}} tableHeaders={maintHeaderData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                customBtnText={"Clear"} customBtnFunc={clearMaint}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        }else if (tabItem === "lost"){
            lostData.sort((a, b) => sortFunc(a, b, lostSorter.sortColumn, lostSorter.sortOrder));
            setLostRows(<TableBody start={0} end={lostData?.length ? lostData.length : 0} tableData={lostData}
                                checkbox={false} writeAccess={false} unk={"l-"}
                                actionAppearance={() => {}} tableHeaders={lostHeaderData} editModal={() => {}}
                                parentComponent={"Alerts"} dData={dData} checkboxlist={[]} setCheckboxId={() => {}}
                                tzOffset={parseInt(dData?.campusData[campusId]?.tz_offset ? dData?.campusData[campusId]?.tz_offset : 0)} />);
        }
    }, [activeAlarms, lowBatData, otherData, lostData, sorter, lostSorter, count, tabItem, fullscreen]);

    function clearMaint(alarmId, clearId, loadId){
        let payload, ackDateTime;
        handleSubmitActions(clearId, loadId);
        ackDateTime = getUTCTimestamp();
        payload = [{"CampusId": campusToGuid(dData, campusId), "AlarmId": alarmId, "DateModified": ackDateTime, "Deleted": true,
            "AckDateTime": ackDateTime, "ModifiedBy": dData.username, "TTL": getTTL(.5)}];
        ApiRequester({reqEndpoint: "save", Tbl: "Telemetry", PKey: "CampusId", SKey: "AlarmId",
                                Payload: payload, CampusId: campusToGuid(dData, campusId)}).then(data => {
            if(data[0]){
                formSuccess("Maintenance alarm cleared", clearId, loadId);
                setCount(0);
                setLostCount(0);
            }
            else{
                formFail("Error encountered, clear failed", clearId, loadId);
            }
        });
    }

    const tableHeaders = <TableHeaders checkbox={false} headerData={headerData} sortOrder={sorter.sortOrder}
                                       borderBuffer={true} sortColumn={sorter.sortColumn}
                                       sorter={sorter} setSorter={setSorter} />
    const tableMaintHeaders = <TableHeaders checkbox={false} headerData={maintHeaderData} sortOrder={sorter.sortOrder}
                                       borderBuffer={true} sortColumn={sorter.sortColumn}
                                       sorter={sorter} setSorter={setSorter} />
    const tableLostHeaders = <TableHeaders checkbox={false} headerData={lostHeaderData} sortOrder={lostSorter.sortOrder}
                                       borderBuffer={true} sortColumn={lostSorter.sortColumn}
                                       sorter={lostSorter} setSorter={setLostSorter} />

    const headerContent = (
        <div className={"d-flex justify-content-between"}>
            <div>
                {csvButton}
            </div>
            {fullscreen
                ?
                <div>
                    <Button text={"Exit Station Mode"} class={"btn btn-primary"} icon={true}
                            iconClass={"fe fe-minimize-2 me-2"} onClick={() => setFullScreen(false)}/>
                    <div className={"form-check mt-3"}>
                        <input className={"form-check-input table-checkbox"} type={"checkbox"} checked={soundOff}
                               name={"sound-on-checkbox"} id={"sound-on-checkbox"}
                               style={{width: "20px", height: "20px"}}
                               onChange={(e) => {
                                   setSoundOff(e.target.checked);
                                   setSoundsMute(dData, e.target.checked);
                               }}/>
                        <label className={"form-check-label"} htmlFor={"sound-on-checkbox"}>Mute Sounds</label>
                    </div>
                </div>
                : <div>
                    <Button text={"Enter Station Mode"} class={"btn btn-primary"} icon={true}
                            iconClass={"fe fe-maximize-2 me-2"} onClick={() => setFullScreen(true)}/>
                    <div className={"form-check mt-3"}>
                        <input className={"form-check-input table-checkbox"} type={"checkbox"} checked={soundOff}
                               name={"sound-on-checkbox"} id={"sound-on-checkbox"}
                               style={{width: "20px", height: "20px"}}
                               onChange={(e) => {
                                   setSoundOff(e.target.checked);
                                   setSoundsMute(dData, e.target.checked);
                               }}/>
                        <label className={"form-check-label"} htmlFor={"sound-on-checkbox"}>Mute Sounds</label>
                    </div>
                </div>
            }
            {!fullscreen && <CampusDropDown campusId={campusId} setCampusId={(item) => setCampusId(item)}
                            setCount={(item) => {setCount(item)}} pageAction={(e) => {
                                setLostCount(0);
                                if (dData?.campusData[campusId.toString()]?.is_cloud !== dData?.campusData[e.target.value]?.is_cloud){
                                    if (dData?.campusData[e.target.value]?.is_cloud){
                                        window.location.href = "/emergency";
                                    }else{
                                        window.location.href = "/alerts";
                                    }
                                }
            }}/>}
        </div>
    );


    return (
        <>
            {!fullscreen && <Navigation/>}
            <div className={"main-content"}>
                <Header preTitle={"Nurse Call"} Title={"Alerts"} content={headerContent}
                        fullscreen={fullscreen} subTitle={fullscreen ? "" : ""}/>
                <div className={"container-fluid"}>
                    <AlertSyncStatus
                        isConnected={isConnected}
                        isDataStale={isDataStale}
                        hasAlerts={filteredEmergCount > 0}
                        connectionError={connectionError}
                    />
                    <div className={"col"}>
                        <ul className={"nav nav-tabs nav-overflow header-tabs"}>
                            <li key={"set-emerg"} className={"nav-item"}>
                                <button className={tabItem === "emergency" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("emergency")}>
                                    Emergency
                                    <span className={"position-relative translate-middle badge rounded-pill bg-danger"}
                                      style={{left: "15px"}}>{filteredEmergCount > 0 ? filteredEmergCount : ""}</span>
                                </button>
                            </li>
                            <li key={"set-lowbat"} className={"nav-item"}>
                                <button className={tabItem === "lowbat" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("lowbat")}>
                                    Batteries
                                    <span className={"position-relative translate-middle badge rounded-pill bg-primary"}
                                      style={{left: "15px"}}>{lowBatData?.length > 0 ? lowBatData.length : ""}</span>
                                </button>
                            </li>
                            <li key={"set-lost"} className={"nav-item"}>
                                <button className={tabItem === "lost" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("lost")}>
                                    Communication Lost
                                    <span className={"position-relative translate-middle badge rounded-pill bg-primary"}
                                      style={{left: "15px"}}>{lostData?.length > 0 ? lostData.length : ""}</span>
                                </button>
                            </li>
                            <li key={"set-other"} className={"nav-item"}>
                                <button className={tabItem === "other" ? "nav-link active" : "nav-link"}
                                        onClick={() => setTabItem("other")}>
                                    Other
                                    <span className={"position-relative translate-middle badge rounded-pill bg-primary"}
                                      style={{left: "15px"}}>{otherData?.length > 0 ? otherData.length : ""}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                    <div className={"row"}>
                        <div className={tabItem === "emergency" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableHeaders}
                                </thead>
                                <tbody>
                                {emergRows}
                                </tbody>
                            </table>
                        </div>
                        <div className={tabItem === "lowbat" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableMaintHeaders}
                                </thead>
                                <tbody>
                                {lowBatRows}
                                </tbody>
                            </table>
                        </div>
                        <div className={tabItem === "lost" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableLostHeaders}
                                </thead>
                                <tbody>
                                {lostRows}
                                </tbody>
                            </table>
                        </div>
                        <div className={tabItem === "other" ? "d-block" : "d-none"}>
                            <table className={"table table-sm table-white table-hover"}
                                   style={{borderCollapse: "collapse"}}>
                                <thead className={isBannerPresent() ? "sticky-table-headX" : "sticky-table-head"}>
                                {tableMaintHeaders}
                                </thead>
                                <tbody>
                                {otherRows}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className={"footer-spacer"}>

                    </div>
                </div>
                <Footer/>
                {/* Floating Help Button - Quarter Circle Corner Shape */}
                <button 
                    type={"button"} 
                    className={"help-corner-button"}
                    onClick={() => showModal("helpmodal")}
                    title={"Page Help Popup"}
                    aria-label={"Open Help"}
                >
                    <i className={"fe fe-help-circle help-corner-button-icon"}></i>
                </button>
                <Suspense fallback={"Loading..."}>
                    <ToastContainer position={"bottom-right"} theme={"colored"} newestOnTop={true} pauseOnHover={true}
                                    autoClose={10000} closeOnClick={true}/>
                </Suspense>
            </div>
            <div className={"modal fade help-modal-pop"} id={"helpmodal"} data-bs-backdrop={"static"} data-bs-keyboard={"false"}
                 tabIndex={-1} aria-labelledby={"staticHelpBackdropLabel"} aria-hidden={"true"}>
                <div className={"modal-dialog modal-lg"}>
                    <div className={"modal-content"}>
                        <div className={"modal-header bg-pal"}>
                            <h2 className={"modal-title"} id={"staticHelpBackdropLabel"}>Alerts Page Help</h2>
                            <button type={"button"} className={"btn-close"} id={"helpClose"} data-bs-dismiss={"modal"}
                                    aria-label={"Close"}/>
                        </div>
                        <div className={"modal-body"}>
                            <div className={"mb-4"}>
                                <h4 className={"text-primary mb-3"}>
                                    <i className={"fe fe-info me-2"}></i>Getting Started
                                </h4>
                                <p className={"mb-2"}>
                                    The Alerts page displays all active alerts in your system. Use the tabs to view different types of alerts: 
                                    Emergency, Batteries, Communication Lost, and Other maintenance alerts.
                                </p>
                            </div>
                            
                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-layers me-2 text-primary"}></i>Alert Tabs
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        <strong>Emergency:</strong> Critical alerts requiring immediate attention (pull cords, emergency buttons, etc.).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Batteries:</strong> Low battery warnings for devices.
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Communication Lost:</strong> Devices that have lost communication with the system.
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Other:</strong> Other maintenance and system alerts.
                                    </li>
                                    <li className={"mb-2"}>
                                        Badge numbers show the count of active alerts in each category.
                                    </li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-list me-2 text-primary"}></i>Alert Information
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        <strong>Event Date/Time:</strong> When the alert was triggered.
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Duration:</strong> How long the alert has been active.
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Type:</strong> The category and specific type of alert.
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Residents:</strong> Associated residents (click to view profile).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Device/Serial #:</strong> The device that triggered the alert (click to view device profile).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Location/Desc:</strong> Where the alert occurred (click to view area profile).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Near:</strong> Nearby area information.
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Accepted:</strong> Which user accepted the alert (if applicable).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Status:</strong> Current alert status (Waiting, Overdue, Accepted, Cleared, Arrived).
                                    </li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-info me-2 text-primary"}></i>Alert Status
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        <strong>Waiting:</strong> Alert is waiting to be accepted or cleared (yellow).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Overdue:</strong> Alert has been active over 20 minutes (red).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Accepted:</strong> Alert has been accepted by staff but device is still in alarm (blue).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Cleared:</strong> Device has been cleared (blue).
                                    </li>
                                    <li className={"mb-2"}>
                                        <strong>Arrived:</strong> Staff has accepted and device is cleared, but alert needs to be finished (blue).
                                    </li>
                                    <li className={"mb-2"}>
                                        Hover over status badges to see detailed explanations.
                                    </li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-corner-down-right me-2 text-primary"}></i>Interacting with Alerts
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        Click on resident names to navigate to their profile pages.
                                    </li>
                                    <li className={"mb-2"}>
                                        Click on device names or serial numbers to view device profiles.
                                    </li>
                                    <li className={"mb-2"}>
                                        Click on location/area names to view area profiles.
                                    </li>
                                    <li className={"mb-2"}>
                                        For maintenance alerts (Batteries, Other), use the <strong>"Clear"</strong> button to acknowledge and clear the alert.
                                    </li>
                                    <li className={"mb-2"}>
                                        Click column headers to sort alerts by that column.
                                    </li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-maximize-2 me-2 text-primary"}></i>Station Mode
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        Click <strong>"Enter Station Mode"</strong> for a fullscreen view optimized for monitoring stations.
                                    </li>
                                    <li className={"mb-2"}>
                                        Station mode hides the navigation and provides a larger view of alerts.
                                    </li>
                                    <li className={"mb-2"}>
                                        Use <strong>"Exit Station Mode"</strong> to return to normal view.
                                    </li>
                                    <li className={"mb-2"}>
                                        Campus dropdown is hidden in station mode.
                                    </li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-volume-2 me-2 text-primary"}></i>Sound Controls
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        Use the <strong>"Mute Sounds"</strong> checkbox to disable alert notification sounds.
                                    </li>
                                    <li className={"mb-2"}>
                                        Sounds play automatically when new emergency alerts appear.
                                    </li>
                                    <li className={"mb-2"}>
                                        Mute setting is saved to your session preferences.
                                    </li>
                                </ul>
                            </div>

                            <div className={"mb-4"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-download me-2 text-primary"}></i>Exporting Data
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        Click the <strong>"Export"</strong> button to download alert data as a CSV file.
                                    </li>
                                    <li className={"mb-2"}>
                                        The export includes all visible columns for the current tab.
                                    </li>
                                    <li className={"mb-2"}>
                                        Export data includes event time, duration, alert type, residents, device, location, and status.
                                    </li>
                                </ul>
                            </div>

                            <div className={"mb-3"}>
                                <h5 className={"fw-bold mb-2"}>
                                    <i className={"fe fe-settings me-2 text-primary"}></i>Additional Tips
                                </h5>
                                <ul className={"list-unstyled ms-4"}>
                                    <li className={"mb-2"}>
                                        Alerts automatically refresh every 7 seconds to show the latest status.
                                    </li>
                                    <li className={"mb-2"}>
                                        The left border color indicates the alert type/category.
                                    </li>
                                    <li className={"mb-2"}>
                                        Emergency alerts are prioritized and appear at the top of the Emergency tab.
                                    </li>
                                    <li className={"mb-2"}>
                                        Overdue alerts (active over 20 minutes) are highlighted in red.
                                    </li>
                                    <li className={"mb-2"}>
                                        Use the mobile app to accept, clear, and finish alerts for devices configured with AFR (Accept/Finish/Reason) requirements.
                                    </li>
                                    <li className={"mb-2"}>
                                        Communication Lost alerts show devices that haven't communicated with the system recently.
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className={"modal-footer"}>
                            <button type={"button"} className={"btn btn-secondary"} data-bs-dismiss={"modal"}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CurrentAlertsV2;